import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { setImpersonatorId, setRefreshToken as setPersistedRefreshToken, setUser } from "@/src/auth/store/auth-slice";
import { identifyUser } from "@/src/common/lib/identify";
import { getAccessToken, getRefreshToken, httpClient, setAccessToken, setRefreshToken } from "@/src/common/services/http.service";
import { setCommunities } from "@/src/community/store/community-slice";

type EdenClient = typeof httpClient;
export type EdenCall<T> = (client: EdenClient) => Promise<{
	data: T | null;
	error: unknown;
	status: number;
	response: Response;
	headers: HeadersInit | undefined;
}>;

type ExecuteCallError = {
	status: number | "CUSTOM_ERROR";
	data?: unknown;
	error?: string;
};

type ExecuteCallResult<T> = { data: T } | { error: ExecuteCallError };

type RefreshOutcome = "refreshed" | "throttled" | "failed";

const MIN_REFRESH_INTERVAL_MS = 2000;

let refreshPromise: Promise<boolean> | null = null;
let lastRefreshAttemptAt = 0;

function hasValueProperty(value: unknown): value is { value: unknown } {
	return typeof value === "object" && value !== null && "value" in value;
}

async function tryRefreshToken(dispatch: (action: unknown) => void): Promise<boolean> {
	const token = getRefreshToken();
	const refreshArg = token ? { refresh_token: token } : {};

	try {
		const { data, error } = await httpClient.auth.refresh.post(refreshArg);

		if (error || !data) return false;

		setAccessToken(data.access_token);
		setRefreshToken(data.refresh_token);
		dispatch(setPersistedRefreshToken(data.refresh_token));
		dispatch(setUser(data.user));
		dispatch(setImpersonatorId(data.impersonator_id ?? null));
		dispatch(setCommunities(data.communities));
		identifyUser(data.user);

		return true;
	} catch {
		return false;
	}
}

async function attemptRefresh(dispatch: (action: unknown) => void): Promise<RefreshOutcome> {
	if (refreshPromise) {
		const ok = await refreshPromise;
		return ok ? "refreshed" : "failed";
	}

	const now = Date.now();
	if (now - lastRefreshAttemptAt < MIN_REFRESH_INTERVAL_MS) {
		return "throttled";
	}
	lastRefreshAttemptAt = now;

	refreshPromise = tryRefreshToken(dispatch).finally(() => {
		refreshPromise = null;
	});

	const ok = await refreshPromise;
	return ok ? "refreshed" : "failed";
}

async function executeCall(edenCall: EdenCall<unknown>): Promise<ExecuteCallResult<unknown>> {
	try {
		const { data, error, status } = await edenCall(httpClient);

		if (error) {
			const body = hasValueProperty(error) ? error.value : error;
			return { error: { status, data: body } };
		}

		return { data };
	} catch (e) {
		const message = e instanceof Error ? e.message : String(e);
		const customError: ExecuteCallError = { status: "CUSTOM_ERROR", error: message };
		return { error: customError };
	}
}

export const edenBaseQuery = (): BaseQueryFn<EdenCall<unknown>> => async (edenCall, api) => {
	const result = await executeCall(edenCall);

	if ("error" in result && typeof result.error.status === "number" && result.error.status === 401) {
		const outcome = await attemptRefresh(api.dispatch);

		if (outcome === "refreshed") {
			if (getAccessToken()) {
				return executeCall(edenCall);
			}
			return result;
		}

		if (outcome === "throttled") {
			return result;
		}

		const { logOutAndReset } = await import("@/src/auth/store/logout-thunk");
		api.dispatch(logOutAndReset({ silent: true }));
		return result;
	}

	return result;
};
